import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, alpha, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { createWebhook, deleteWebhook, getWebhookActivity, listWebhookEvents, listWebhooks, replayWebhookDelivery, testWebhook, toggleWebhook, updateWebhook } from 'api/features';
import { GREEN, MONO, ago } from './scanUi';
import { STATUS, eventLabel, failingNotes, logRows, rateOf, recentBars, asPayload } from './webhookLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const EMPTY = { id: null, name: '', url: '', secret: '', events: [] };
const fail = (e, fb) => e?.response?.data?.detail || Object.values(e?.response?.data || {}).flat()[0] || e?.message || fb;
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;

export default function WebhooksPage() {
  const [hooks, setHooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [activity, setActivity] = useState({ results: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState('');
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const [h, a] = await Promise.all([listWebhooks(), getWebhookActivity(100)]);
      setHooks(Array.isArray(h) ? h : h.results || []);
      setActivity(a);
    } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not load your webhooks.') }); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); listWebhookEvents().then(setEvents).catch(() => {}); }, [load]);

  const act = async (id, fn, ok) => {
    setBusy(id);
    try { const r = await fn(); setToast({ severity: 'success', message: typeof ok === 'function' ? ok(r) : ok }); } catch (e) { setToast({ severity: 'error', message: fail(e, 'That did not work.') }); }
    setBusy(null);
    load();
  };
  const save = async () => {
    setFormError('');
    try {
      const payload = asPayload(form, form.id);
      await (form.id ? updateWebhook(form.id, payload) : createWebhook(payload));
      setForm(null); setToast({ severity: 'success', message: form.id ? 'Webhook updated.' : 'Webhook added.' }); load();
    } catch (e) { setFormError(fail(e, 'Could not save.')); }
  };
  const remove = (h) => { if (window.confirm(`Delete ${h.name}? Its delivery history goes with it.`)) act(h.id, () => deleteWebhook(h.id), 'Webhook deleted.'); };

  const s = activity.summary;
  const notes = useMemo(() => failingNotes(hooks), [hooks]);
  const rows = logRows(activity.results, onlyFailed);
  const missed = activity.results.filter((d) => !d.ok).length;
  const stat = (label, value, sub, color) => (
    <Box sx={{ ...panel, p: 2.2, flex: '1 1 190px' }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: MONO, color }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{sub}</Typography>
    </Box>
  );

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Webhooks</Typography>
        <Typography color="text.secondary" variant="body2">Send events to Slack, Jira, PagerDuty or your own system, and see that they arrive</Typography>
      </Box>

      <Stack direction="row" gap={2} flexWrap="wrap">
        {stat('Working', s.active ?? 0, 'webhooks', GREEN)}
        {stat('Failing', s.failing ?? 0, s.failing ? 'needs attention' : 'none', s.failing ? '#ef4444' : undefined)}
        {stat('Delivered, last 24 h', s.deliveries_24h ?? 0, s.deliveries_24h ? `${Math.round((s.ok_24h / s.deliveries_24h) * 100)}% arrived` : 'nothing sent yet')}
        {stat('Average speed', s.avg_ms_24h ? `${s.avg_ms_24h} ms` : '–', 'per delivery')}
      </Stack>

      {notes.map((n) => (
        <Alert key={n.id} severity="error" action={<Button color="inherit" size="small" onClick={() => { setOnlyFailed(true); document.getElementById('delivery-log')?.scrollIntoView({ behavior: 'smooth' }); }}>See what failed</Button>}>
          <b>{n.name} is failing.</b> {n.reason}
        </Alert>
      ))}

      <Box sx={{ ...panel, overflow: 'hidden' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2.2 }}>
          <Typography sx={{ fontWeight: 700 }}>Your webhooks</Typography>
          <Button variant="contained" onClick={() => setForm({ ...EMPTY })} sx={{ fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>Add a webhook</Button>
        </Stack>
        {!hooks.length && <Typography color="text.secondary" sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>No webhooks yet. Add one and pick which events it should receive.</Typography>}
        {hooks.map((h) => {
          const st = STATUS[h.status] || STATUS.active;
          const rate = rateOf(h);
          return (
            <Stack key={h.id} direction="row" alignItems="center" gap={2.5} flexWrap="wrap" sx={{ px: 2.8, py: 1.8, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ flex: '2 1 240px', minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>{h.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO, wordBreak: 'break-all' }}>{h.url.length > 60 ? `${h.url.slice(0, 60)}…` : h.url}</Typography>
              </Box>
              <Stack direction="row" gap={0.6} flexWrap="wrap" sx={{ flex: '2 1 240px' }}>
                {(h.events || []).map((e) => <Chip key={e} size="small" label={e === '*' ? 'Everything' : eventLabel(events, e)} />)}
                {!(h.events || []).length && <Typography variant="caption" color="text.secondary">No events picked</Typography>}
              </Stack>
              <Stack direction="row" gap={0.4} alignItems="flex-end" sx={{ height: 26 }}>
                {recentBars(activity.results, h.id).map((d) => <Box key={d.id} sx={{ width: 6, height: d.ok ? 10 + (d.id % 5) * 3 : 10, borderRadius: 0.5, bgcolor: d.ok ? GREEN : '#ef4444' }} />)}
              </Stack>
              <Box sx={{ width: 120 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{rate === null ? 'No deliveries' : `${rate}% worked`}</Typography>
                <Typography variant="caption" color="text.secondary">{h.last_delivery_at ? ago(h.last_delivery_at) : 'never sent'}</Typography>
              </Box>
              <Pill color={st.color}>{st.label}</Pill>
              <Stack direction="row" gap={0.8}>
                <Button size="small" variant="outlined" color="inherit" disabled={busy === h.id || h.status === 'disabled'} onClick={() => act(h.id, () => testWebhook(h.id), (r) => (r.success ? `Test arrived (HTTP ${r.status_code}).` : `Test was refused (HTTP ${r.status_code}).`))}>Send test</Button>
                <Button size="small" variant="outlined" color="inherit" disabled={busy === h.id} onClick={() => act(h.id, () => toggleWebhook(h.id), h.status === 'disabled' ? 'Webhook resumed.' : 'Webhook paused.')}>{h.status === 'disabled' ? 'Resume' : 'Pause'}</Button>
                <Button size="small" color="inherit" onClick={() => setForm({ ...EMPTY, id: h.id, name: h.name, url: h.url, events: h.events || [] })}>Edit</Button>
                <Button size="small" color="error" onClick={() => remove(h)}>Delete</Button>
              </Stack>
            </Stack>
          );
        })}
      </Box>

      <Box id="delivery-log" sx={{ ...panel, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ p: 2.2 }}>
          <Typography sx={{ fontWeight: 700, mr: 'auto' }}>Recent deliveries</Typography>
          {[[false, 'All', activity.results.length], [true, 'Did not arrive', missed]].map(([v, label, n]) => (
            <Button key={label} onClick={() => setOnlyFailed(v)} sx={{ borderRadius: 99, px: 2, textTransform: 'none', fontWeight: 600, border: 1, borderColor: onlyFailed === v ? 'text.secondary' : 'divider', color: onlyFailed === v ? 'text.primary' : 'text.secondary' }}>{label} {n}</Button>
          ))}
        </Stack>
        {!rows.length && <Typography color="text.secondary" sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>{onlyFailed ? 'Everything arrived.' : 'Nothing has been sent yet. Use "Send test" on a webhook to try it.'}</Typography>}
        {rows.map((d) => (
          <Stack key={d.id} direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ px: 2.8, py: 1.4, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d.ok ? GREEN : '#ef4444' }} />
            <Typography sx={{ flex: '1 1 180px', fontWeight: 600, fontSize: 14 }}>{d.event === 'test' ? 'Test message' : eventLabel(events, d.event)}</Typography>
            <Typography sx={{ flex: '1 1 140px', fontSize: 13 }} color="text.secondary">{d.webhook_name}</Typography>
            <Pill color={d.ok ? GREEN : '#ef4444'}>{d.status_code ? `HTTP ${d.status_code}` : 'No answer'}</Pill>
            <Typography sx={{ width: 70, fontFamily: MONO, fontSize: 12.5 }} color="text.secondary">{d.duration_ms ? `${d.duration_ms} ms` : ''}</Typography>
            <Typography sx={{ width: 100, fontSize: 12.5 }} color="text.secondary">{ago(d.created_at)}</Typography>
            <Box sx={{ width: 100 }}>
              {!d.ok && <Button size="small" variant="outlined" color="inherit" disabled={busy === `d${d.id}`} onClick={() => act(`d${d.id}`, () => replayWebhookDelivery(d.id), (r) => (r.sent ? 'Sent again.' : fail({ message: r.error }, 'Still not arriving.')))}>Send again</Button>}
            </Box>
            {!d.ok && d.error && <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%', pl: 3 }}>{d.error}</Typography>}
          </Stack>
        ))}
      </Box>
      <Typography variant="body2" color="text.secondary">Every delivery is signed with your secret (header X-FuzzScan-Signature), so the receiver can check it really came from FuzzScan. A webhook is marked failing after 5 failed deliveries in a row and recovers with the next good one.</Typography>

      <Dialog open={Boolean(form)} onClose={() => setForm(null)} fullWidth maxWidth="sm">
        <DialogTitle>{form?.id ? 'Edit webhook' : 'Add a webhook'}</DialogTitle>
        <DialogContent>
          {form && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField size="small" label="Name" placeholder="Slack #security" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <TextField size="small" label="Web address" placeholder="https://hooks.slack.com/services/..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
              <TextField size="small" type="password" label={form.id ? 'New secret (leave empty to keep the current one)' : 'Secret (optional)'} helperText="Used to sign every delivery." value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })} autoComplete="new-password" />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Send me</Typography>
              <Stack direction="row" gap={0.8} flexWrap="wrap">
                {events.map((e) => {
                  const on = form.events.includes(e.id);
                  return <Chip key={e.id} label={e.label} clickable color={on ? 'success' : 'default'} variant={on ? 'filled' : 'outlined'} onClick={() => setForm({ ...form, events: on ? form.events.filter((x) => x !== e.id) : [...form.events, e.id] })} />;
                })}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setForm(null)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={!form?.name.trim() || !form?.url.trim() || !form?.events.length} onClick={save}>{form?.id ? 'Save' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>{toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}</Snackbar>
    </Stack>
  );
}
